/**
 * Resolve the current employee's permissions for a settings domain.
 *
 * Returns the boolean flags every settings screen needs: view/manage, plus the
 * optional credential and order-lifecycle sub-permissions where they exist.
 */
import { usePermissions } from '@/shared/permissions';
import type { PermissionCode } from '@/shared/permissions';

import { STORE_SETTINGS_PERMISSIONS } from '../model/permissions';
import type { StoreSettingsDomain } from '../model/permissions';

export interface DomainPermissions {
  canView: boolean;
  canManage: boolean;
  canManageCredentials: boolean;
  canManageLifecycle: boolean;
}

export function useDomainPermissions(domain: StoreSettingsDomain): DomainPermissions {
  const { hasPermission } = usePermissions();
  const perms = STORE_SETTINGS_PERMISSIONS[domain];
  const optional = perms as Record<string, PermissionCode | undefined>;

  return {
    canView: hasPermission(perms.view),
    canManage: hasPermission(perms.manage),
    canManageCredentials: optional.credentials ? hasPermission(optional.credentials) : false,
    canManageLifecycle: optional.lifecycle ? hasPermission(optional.lifecycle) : false,
  };
}
